/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { isEqual, cloneDeep } from 'lodash';
import { IoCloseSharp } from 'react-icons/io5';
import { connect } from 'react-redux';
import { updateModal } from '../../actions/modalActions';
import {
  uploadOrganizationImage,
  updateOrganization,
  addOrgRole,
  removeOrgRole
} from '../../actions/organizationActions';
import { updateFormError } from '../../actions/userActions';
import ModalNavigation from '../Common/modalNavigation';
import ShortNameField from '../Common/shortNameField';
import OrganizationTypesTab from './organizationTypesTab';
import DetailsTab from './detailsTab';
import OrganizationMembersTab from './organizationMembersTab';
import { allows } from '../../utils/authorization';
import GuideMe from '../Tour/GuideMe';

class OrganizationCard extends React.Component {
  constructor(props) {
    super(props);

    const currentModalInfo = props.modalInfo.organizationCard;

    this.state = {
      organization: {
        id: -1,
        name: ''
      },
      inputEdited: false,
      activeTab: currentModalInfo?.tab || 'details'
    };
  }

  componentDidMount() {
    const { organization } = this.props;
    if (organization.id > 0) {
      this.setState({ organization });
    }
  }

  componentDidUpdate(prevProps) {
    const { organization: prevOrgnization } = prevProps;
    const { organization: propsOrganization } = this.props;
    if (
      propsOrganization.id > 0 &&
      !isEqual(prevOrgnization, propsOrganization)
    ) {
      this.setState({ organization: propsOrganization });
    }
  }

  handleFieldChange = (e) => {
    const { organization } = this.state;
    let field = e;
    if (e.target && e.target.name) {
      field = { [e.target.name]: e.target.value };
    } else if (e.short_name) {
      field = { name: e.short_name };
    }
    const organizationCopy = { ...organization, ...field };
    this.setState({ organization: organizationCopy });
  };

  handleSave = (event) => {
    event.preventDefault();
    const { organization } = this.state;
    if (isEqual(organization, this.props.organization)) {
      return this.closeModal();
    }

    if (organization.name) {
      if (organization.id > 0) {
        this.props.updateOrganization(organization.id, organization);
      }
      this.closeModal();
    } else {
      this.setState({ inputEdited: true });
    }
  };

  closeModal = () => {
    // clear any user form errors
    this.props.updateFormError({});
    this.props.onClose();
  };

  changeTab = (tabName) => {
    if (tabName === this.state.activeTab) return;
    // this.tour.complete();
    this.setState({ activeTab: tabName }, () => {
      this.props.updateModal('organizationCard', tabName);
    });
  };

  changeRole = (userId, roleType) => {
    const { organization, users } = this.props;

    let user = users.find((member) => member.id === userId);
    user = cloneDeep(user);

    const role = user.organization_roles.find(
      (r) => r.type === roleType && r.organization_id === organization.id
    );

    if (role) {
      this.props.removeOrgRole(organization.id, role.id, user.id);
    } else {
      this.props.addOrgRole(organization.id, user.id, roleType);
    }
  };

  renderActiveTab = () => {
    const { showTrash, isLoading, users } = this.props;
    const { organization, activeTab } = this.state;
    let { allowEdit } = this.props;
    allowEdit ||= allows('update', organization, 'organization');

    switch (activeTab) {
      case 'details':
        return (
          <DetailsTab
            organization={organization}
            allowEdit={allowEdit}
            handleFieldChange={this.handleFieldChange}
          />
        );
      case 'types':
        return (
          <OrganizationTypesTab
            organization={organization}
            showTrash={showTrash}
          />
        );
      case 'members':
        return (
          <OrganizationMembersTab
            organization={organization}
            users={users}
            changeRole={this.changeRole}
            isSending={isLoading}
          />
        );
      default:
        return (
          <DetailsTab
            organization={organization}
            allowEdit={allowEdit}
            handleFieldChange={this.handleFieldChange}
          />
        );
    }
  };

  render = () => {
    const { organization, activeTab } = this.state;
    let { allowEdit } = this.props;

    allowEdit ||= allows('update', organization, 'organization');
    if (!this.props.isOpen) {
      return null;
    }
    const { isLoading, showTrash } = this.props;

    return (
      <div
        className="detail-modal team-modal organization-modal"
        tabIndex="-1"
        role="dialog"
      >
        <div className="detail-dialog" role="document">
          {isLoading ? <div id="loader" /> : null}
          <div
            className={`modal-content profile-content ${
              isLoading ? 'blur-loading' : ''
            }`}
          >
            <div className="modal-header profile-header detail-header org-modal-header">
              <GuideMe />
              <div className="detail-close">
                <button
                  type="button"
                  className="btn-close"
                  onClick={organization.id ? this.handleSave : this.closeModal}
                >
                  <IoCloseSharp size="2em" />
                </button>
              </div>
            </div>
            <div className={`modal-body organization-modal-body ${activeTab}`}>
              <div className="modal-body-input">
                <ShortNameField
                  shortName={organization.name}
                  onNameChange={this.handleFieldChange}
                  inputEdited={this.state.inputEdited}
                  label="Name"
                  fieldName="name"
                  allowEdit={allowEdit}
                />
              </div>
              <ModalNavigation
                tabs={['details', 'members', 'types']}
                activeTab={activeTab || 'details'}
                changeTab={this.changeTab}
              />
              {this.renderActiveTab()}
            </div>
            {!organization.id && (
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={this.handleSave}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={this.closeModal}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
}

OrganizationCard.propTypes = {
  organization: PropTypes.object,
  allowEdit: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
};

OrganizationCard.defaultProps = {
  organization: {
    id: -1,
    name: ''
  }
};

const mapStateToProps = (state, ownProps) => ({
  organization: ownProps.id
    ? state.organizationReducer.organizations.find(
        (org) => org.id === ownProps.id
      )
    : null,
  isLoading:
    state.organizationReducer.isFetching || state.userReducer.isFetching,
  currentUser: state.userReducer.user,
  showTrash: state.trashReducer.showTrash,
  users: state.userReducer.users,
  modalInfo: state.modalReducer.modalInfo,
  tour: state.organizationReducer.tour
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      uploadOrganizationImage,
      updateOrganization,
      updateModal,
      addOrgRole,
      removeOrgRole,
      updateFormError
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(OrganizationCard);
